import { Fragment, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Activity, ExternalLink, Film, UserCircle2 } from 'lucide-react';
import { useChatThread } from '@/hooks/useChatThread';
import { CopyButton } from '@/components/ui/CopyButton';

const ENTITY_LINK =
  'inline-flex h-6 items-center gap-1 rounded-md border border-border bg-transparent px-2 align-middle text-[11px] font-medium hover:bg-muted';

type Block =
  | { type: 'p'; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: { num: string; text: string }[] }
  | { type: 'h'; level: number; text: string };

function parseBlocks(src: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ type: 'p', lines: para });
      para = [];
    }
  };

  for (const line of src.replace(/\r\n/g, '\n').split('\n')) {
    if (!line.trim()) {
      flush();
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flush();
      blocks.push({ type: 'h', level: h[1]!.length, text: h[2]! });
      continue;
    }
    const ol = /^\s*(\d+)[.)-]\s+(.*)$/.exec(line);
    if (ol) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'ol') last.items.push({ num: ol[1]!, text: ol[2]! });
      else blocks.push({ type: 'ol', items: [{ num: ol[1]!, text: ol[2]! }] });
      continue;
    }
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (ul) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'ul') last.items.push(ul[1]!);
      else blocks.push({ type: 'ul', items: [ul[1]!] });
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
}

const INLINE_RE =
  /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|(\/(?:content|personas|runs)(?:\/[A-Za-z0-9_-]*)?)|\b((?:content|persona|account|workflow|thread)_[0-9a-fA-F][0-9a-fA-F-]{10,})|(https?:\/\/[^\s)]+)/g;

type EntityKind =
  | 'content'
  | 'persona'
  | 'workflow'
  | 'content-list'
  | 'persona-list'
  | 'runs-list'
  | 'text'
  | 'none';

function looksLikeId(s: string): boolean {
  return (
    /^(?:content|persona|account|workflow|thread)_[0-9a-fA-F]/.test(s) ||
    /^[0-9a-fA-F]{8}-[0-9a-fA-F-]+$/.test(s)
  );
}

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

function classifyEntity(raw: string): { kind: EntityKind; id: string } {
  let m: RegExpExecArray | null;
  if ((m = /^\/content\/(.+)$/.exec(raw)))
    return looksLikeId(m[1]!) ? { kind: 'content', id: m[1]! } : { kind: 'text', id: raw };
  if ((m = /^\/personas\/(.+)$/.exec(raw)))
    return looksLikeId(m[1]!) ? { kind: 'persona', id: m[1]! } : { kind: 'text', id: raw };
  if ((m = /^\/runs\/(.+)$/.exec(raw))) return { kind: 'workflow', id: m[1]! };
  if (/^\/content\/?$/.test(raw)) return { kind: 'content-list', id: '' };
  if (/^\/personas\/?$/.test(raw)) return { kind: 'persona-list', id: '' };
  if (/^\/runs\/?$/.test(raw)) return { kind: 'runs-list', id: '' };
  if (/^content_/.test(raw)) return { kind: 'content', id: raw };
  if (/^persona_/.test(raw)) return { kind: 'persona', id: raw };
  return { kind: 'none', id: raw };
}

function EntityRef({ raw }: { raw: string }) {
  const { setOpen } = useChatThread();
  const { kind, id } = classifyEntity(raw);
 
  const copyValue = raw.startsWith('/') ? `${ORIGIN}${raw}` : id;

  if (kind === 'text') return <>{raw}</>;

  if (kind === 'content') {
    return (
      <span className="inline-flex items-center gap-1 align-middle">
        <Link
          to="/content/$contentId"
          params={{ contentId: id }}
          onClick={() => setOpen(false)}
          title={`${ORIGIN}/content/${id}`}
          className={ENTITY_LINK}
        >
          <Film className="h-3 w-3" />
          Review content
          <ExternalLink className="h-2.5 w-2.5 opacity-60" />
        </Link>
        <CopyButton value={copyValue} label={`Copy ${copyValue}`} />
      </span>
    );
  }
  if (kind === 'persona') {
    return (
      <span className="inline-flex items-center gap-1 align-middle">
        <Link
          to="/personas/$personaId"
          params={{ personaId: id }}
          onClick={() => setOpen(false)}
          title={`${ORIGIN}/personas/${id}`}
          className={ENTITY_LINK}
        >
          <UserCircle2 className="h-3 w-3" />
          View persona
          <ExternalLink className="h-2.5 w-2.5 opacity-60" />
        </Link>
        <CopyButton value={copyValue} label={`Copy ${copyValue}`} />
      </span>
    );
  }
  if (kind === 'workflow') {
    return (
      <span className="inline-flex items-center gap-1 align-middle">
        <Link
          to="/runs/$workflowId"
          params={{ workflowId: id }}
          onClick={() => setOpen(false)}
          className={ENTITY_LINK}
        >
          <Activity className="h-3 w-3" />
          View workflow
          <ExternalLink className="h-2.5 w-2.5 opacity-60" />
        </Link>
        <CopyButton value={id} label={`Copy ${id}`} />
      </span>
    );
  }
  if (kind === 'persona-list') {
    return (
      <Link to="/personas" onClick={() => setOpen(false)} className={ENTITY_LINK}>
        <UserCircle2 className="h-3 w-3" />
        Browse personas
        <ExternalLink className="h-2.5 w-2.5 opacity-60" />
      </Link>
    );
  }
  if (kind === 'content-list') {
    return (
      <Link to="/content" onClick={() => setOpen(false)} className={ENTITY_LINK}>
        <Film className="h-3 w-3" />
        Browse content
        <ExternalLink className="h-2.5 w-2.5 opacity-60" />
      </Link>
    );
  }
  if (kind === 'runs-list') {
    return (
      <Link to="/runs" onClick={() => setOpen(false)} className={ENTITY_LINK}>
        <Activity className="h-3 w-3" />
        View runs
        <ExternalLink className="h-2.5 w-2.5 opacity-60" />
      </Link>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <code className="rounded bg-background/60 px-1 py-0.5 font-mono text-[0.85em] text-foreground">
        {id}
      </code>
      <CopyButton value={id} label={`Copy ${id}`} />
    </span>
  );
}

function MdLink({ href, label }: { href: string; label: string }) {
  if (/^https?:\/\//.test(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-0.5 font-medium text-primary underline-offset-2 hover:underline"
      >
        {label}
        <ExternalLink className="h-2.5 w-2.5 opacity-70" />
      </a>
    );
  }
  
  if (/^\/(content|personas|runs)(\/|$)/.test(href)) return <EntityRef raw={href} />;
  return <span>{label}</span>;
}

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  INLINE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<MdLink key={key++} href={m[2]!} label={m[1]} />);
    } else if (m[3] !== undefined) {
      out.push(
        <strong key={key++} className="font-semibold text-foreground">
          {m[3]}
        </strong>,
      );
    } else if (m[4] !== undefined) {
      out.push(
        <code
          key={key++}
          className="rounded bg-background/60 px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {m[4]}
        </code>,
      );
    } else if (m[5] !== undefined) {
      out.push(<EntityRef key={key++} raw={m[5]} />);
    } else if (m[6] !== undefined) {
      out.push(<EntityRef key={key++} raw={m[6]} />);
    } else if (m[7] !== undefined) {
      out.push(<MdLink key={key++} href={m[7]} label={m[7]} />);
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case 'h':
      return (
        <p
          className={
            block.level <= 2
              ? 'text-[0.95rem] font-semibold text-foreground'
              : 'text-sm font-semibold text-foreground'
          }
        >
          {renderInline(block.text)}
        </p>
      );
    case 'ol':
      return (
        <ol className="space-y-1">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="select-none font-medium text-muted-foreground">{it.num}.</span>
              <span className="min-w-0">{renderInline(it.text)}</span>
            </li>
          ))}
        </ol>
      );
    case 'ul':
      return (
        <ul className="space-y-1">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="select-none text-muted-foreground">•</span>
              <span className="min-w-0">{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    case 'p':
      return (
        <p>
          {block.lines.map((l, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {renderInline(l)}
            </Fragment>
          ))}
        </p>
      );
  }
}

export function MarkdownMessage({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {parseBlocks(text).map((b, i) => (
        <BlockView key={i} block={b} />
      ))}
    </div>
  );
}
